/**
 * CSV writing shared by every feature that exports one.
 *
 * Extracted from `features/dashboard/sections/scholarships/rosterCsv.ts` when Trusted Contacts
 * became the second exporter. The escaping here is a **security control**, not a formatting
 * convenience — a CWE-1236 fix applied to one private copy would leave the other exploitable, and
 * neither file would say the other existed. Column choice and row mapping stay with each feature;
 * only the encoding lives here.
 */

// A leading one of these makes Excel, Sheets and LibreOffice treat the cell as a formula rather
// than as text. Tab and carriage return are included because both are stripped before the parse,
// exposing whatever follows them.
const FORMULA_TRIGGERS = /^[=+\-@\t\r]/;

// Anything that is unambiguously a number is left alone, so a negative value stays numeric
// instead of being turned into text by the guard below.
const PLAIN_NUMBER = /^-?\d+(\.\d+)?$/;

/**
 * Neutralises a spreadsheet formula in an exported value (CWE-1236).
 *
 * A player named `=cmd|'/c calc'!A1`, or a note starting `=HYPERLINK(...)`, executes when the
 * file is opened rather than when it is written — so escaping quotes is not enough on its own.
 * This is a live path: prospect names and notes arrive from LLM roster extraction and from the
 * public submission form, and directory contacts are typed by hand or lifted verbatim off a phone
 * address book. None of that is trusted input.
 *
 * A leading apostrophe is the conventional fix. The spreadsheet consumes it on import, so the
 * cell reads as the original text while never being evaluated.
 */
const neutralizeFormula = (value: string): string =>
  FORMULA_TRIGGERS.test(value) && !PLAIN_NUMBER.test(value) ? `'${value}` : value;

/**
 * Escapes one CSV field.
 *
 * Quotes whenever the value contains a comma, a quote, a newline, or leading/trailing spaces that
 * a reader would otherwise strip. Names routinely contain commas ("Miller, Jr.") and notes contain
 * newlines, so this is the common case rather than an edge one.
 *
 * Formula neutralisation happens FIRST, so the apostrophe lands inside the quotes and is what the
 * spreadsheet actually sees at the start of the cell.
 */
export const escapeCsvField = (raw: string): string => {
  if (raw.length === 0) return '';
  const value = neutralizeFormula(raw);
  const needsQuotes = /[",\n\r]/.test(value) || value !== value.trim();
  if (!needsQuotes) return value;
  return `"${value.replace(/"/g, '""')}"`;
};

/**
 * Joins already-built rows — header included — into CSV text.
 *
 * CRLF: Excel on Windows treats a bare LF as one long line in some locales, and these files exist
 * to be opened in a spreadsheet.
 */
export const toCsvText = (rows: string[][]): string =>
  rows.map((row) => row.map(escapeCsvField).join(',')).join('\r\n');
